"use client";

import Link from "next/link";
import { ArrowUp, ArrowDown, Copy as CopyIcon } from "lucide-react";
import type { TapeEvent } from "./types";
import { useT } from "@/hooks/useT";

/**
 * HeroV3BottomStrip — single-line scrolling tape of trades, sitting at
 * the seam between the hero and the next section.
 *
 * Why: after the screen-filling hero, a tight horizontal strip resets
 * attention and keeps the "live" sensation flowing into Market Pulse
 * below. Each row links to the market; scrolling pauses on hover so
 * users can actually click.
 */
export default function HeroV3BottomStrip({ events }: { events: TapeEvent[] }) {
  const t = useT();
  if (!events || events.length === 0) return null;
  const loop = [...events, ...events, ...events]; // 3× so marquee stays dense even with small N

  return (
    <div className="relative border-t border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0a0a0b] overflow-hidden">
      {/* "Live trades" label — anchored as an opaque overlay on the left
          so the marquee track can scroll behind it without bleeding the
          text. Without this, the label sat inside the same flex row as
          the track and the cells visibly overlapped the badge as they
          looped through the leftmost slot. */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-20 flex items-center pl-6 pr-8 bg-gray-50 dark:bg-[#0a0a0b]">
        <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          {t("home.strip.liveTrades")}
        </span>
      </div>
      {/* Soft fade right after the label so the first marquee cell
          doesn't collide visually with the badge. */}
      <div className="pointer-events-none absolute inset-y-0 left-[120px] w-12 z-20 bg-gradient-to-r from-gray-50 dark:from-[#0a0a0b] to-transparent" />
      {/* Right-side fade */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-gray-50 dark:from-[#0a0a0b] to-transparent" />

      <div className="group flex items-center overflow-hidden pl-[160px]">
        <div
          className="flex items-center gap-5 py-3 whitespace-nowrap motion-reduce:animate-none group-hover:[animation-play-state:paused]"
          style={{ animation: "hv3-strip 80s linear infinite" }}
        >
          {loop.map((e, i) => (
            <StripCell key={`${e.id}-${i}`} event={e} />
          ))}
        </div>
      </div>

      <style jsx global>{`
        @keyframes hv3-strip {
          from {
            transform: translate3d(0, 0, 0);
          }
          to {
            transform: translate3d(-33.3333%, 0, 0);
          }
        }
      `}</style>
    </div>
  );
}

function StripCell({ event }: { event: TapeEvent }) {
  const color =
    event.kind === "sell"
      ? "#FF1744"
      : event.kind === "copytrade"
      ? "#7C3AED"
      : "#00C853";
  const Icon =
    event.kind === "sell" ? ArrowDown : event.kind === "copytrade" ? CopyIcon : ArrowUp;

  // Separate links: actor name routes to their trader profile; the market
  // title and trailing arrow route to the event page. Keeps the UX
  // predictable — click what you see, go where you'd expect.
  const traderHref = event.actor ? `/traders/${event.actor}` : null;
  // Build the right event URL. The activity stream's marketSlug points
  // at the sub-market, which 404s on multi-outcome events. Use the
  // enriched eventSlug + isMultiOutcome from the backend to land on
  // /polymarket/event/<eventSlug>(?market=<marketSlug> when needed).
  const eventHref = (() => {
    if (event.eventSlug) {
      const base = `/polymarket/event/${event.eventSlug}`;
      return event.isMultiOutcome && event.marketSlug
        ? `${base}?market=${event.marketSlug}`
        : base;
    }
    // No eventSlug fallback — point at the canonical /polymarket/event/
    // route directly. /event/<slug> 301-redirects, costing an extra
    // round-trip on every click from the home strip.
    return event.marketSlug ? `/polymarket/event/${event.marketSlug}` : "/activity";
  })();

  return (
    <span className="inline-flex items-center gap-2 shrink-0 text-[12px]">
      <span
        className="w-4 h-4 rounded-[3px] flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: color }}
      >
        <Icon className="w-2.5 h-2.5 text-black" strokeWidth={3.5} />
      </span>
      {traderHref ? (
        <Link
          href={traderHref}
          className="font-inter-tight font-semibold text-gray-900 dark:text-white hover:underline underline-offset-2"
        >
          {event.actorName}
        </Link>
      ) : (
        <span className="font-inter-tight font-semibold text-gray-900 dark:text-white">
          {event.actorName}
        </span>
      )}
      <span className="font-inter-tight text-gray-500 dark:text-gray-400">
        {event.verb}
      </span>
      <Link
        href={eventHref}
        className="font-inter-tight text-gray-700 dark:text-gray-300 max-w-[280px] truncate hover:text-gray-900 dark:hover:text-white transition-colors"
      >
        “{event.marketTitle}”
      </Link>
      {event.amountUsd > 0 && (
        <span
          className="font-inter-tight font-semibold tabular-nums"
          style={{ color }}
        >
          {formatAmount(event.amountUsd)}
        </span>
      )}
      <span className="text-gray-300 dark:text-white/10">·</span>
    </span>
  );
}

function formatAmount(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}
